import type { MapRef } from "react-map-gl/mapbox"
import { MapPin, MapPinned, Globe } from "lucide-react"
import { computeDistanceKm, formatDistanceLabel } from "@/lib/explore/utils/format-distance"
import type { AddressFeature } from "./address-search-bar"

const typeLabel: Record<string, string> = {
  housenumber: "adresse",
  street: "voie",
  municipality: "commune",
  locality: "lieu-dit",
}

const typeIcon: Record<string, React.ReactNode> = {
  housenumber: <MapPin className="w-3.5 h-3.5" />,
  street: <MapPinned className="w-3.5 h-3.5" />,
}

interface AddressResultsDropdownProps {
  results: AddressFeature[]
  highlightedIndex: number
  setHighlightedIndex: (index: number) => void
  selectResult: (feature: AddressFeature) => void
  mapRef: React.RefObject<MapRef | null>
  onHoverResult?: (lngLat: [number, number]) => void
  onLeaveResult?: () => void
}

export const AddressResultsDropdown = ({
  results,
  highlightedIndex,
  setHighlightedIndex,
  selectResult,
  mapRef,
  onHoverResult,
  onLeaveResult,
}: AddressResultsDropdownProps) => {
  if (results.length === 0) {
    return (
      <div className="mt-1.5 bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden map-panel-shadow anim-fade-in">
        <div className="px-4 py-3 text-sm text-center text-gray-400">Aucun resultat trouve</div>
      </div>
    )
  }

  return (
    <div className="mt-1.5 bg-white/95 backdrop-blur-sm rounded-xl overflow-hidden map-panel-shadow anim-fade-in">
      {results.map((feature, index) => {
        const { name, postcode, city, type, id } = feature.properties
        const isHighlighted = index === highlightedIndex
        const mapCenter = mapRef.current?.getCenter?.()
        const dist = mapCenter ? computeDistanceKm([mapCenter.lng, mapCenter.lat], feature.geometry.coordinates) : null
        return (
          <button
            key={id}
            type="button"
            className={`w-full text-left flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors ${
              isHighlighted ? "bg-blue-50" : "bg-transparent"
            } ${index < results.length - 1 ? "border-b border-gray-100" : ""}`}
            onClick={() => {
              onLeaveResult?.()
              selectResult(feature)
            }}
            onMouseEnter={() => {
              setHighlightedIndex(index)
              onHoverResult?.(feature.geometry.coordinates as [number, number])
            }}
            onMouseLeave={() => {
              setHighlightedIndex(-1)
              onLeaveResult?.()
            }}
          >
            <span
              className={`flex items-center justify-center flex-shrink-0 rounded-lg transition-all duration-150 w-7 h-7 ${
                isHighlighted ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-500"
              }`}
            >
              {typeIcon[type] || <Globe className="w-3.5 h-3.5" />}
            </span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-medium truncate text-gray-800">{name}</span>
              {postcode && city && (
                <span className="block text-xs truncate text-gray-400">
                  {postcode} {city}
                </span>
              )}
            </span>
            <span className="flex flex-col items-end gap-0.5 flex-shrink-0">
              <span
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-all duration-100 ${
                  isHighlighted ? "bg-blue-100 text-blue-800" : "bg-gray-100 text-gray-500"
                }`}
              >
                {typeLabel[type] || type}
              </span>
              {dist != null && <span className="text-[10px] text-gray-300">{formatDistanceLabel(dist)}</span>}
            </span>
          </button>
        )
      })}
    </div>
  )
}
